import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, signal, inject } from "@angular/core";
import { IEpisode } from "@project/models/episodes/episode";
import { ConfirmationService, MenuItem } from "primeng/api";
import { OverlayPanel } from "primeng/overlaypanel";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { HistoricalItem } from "@mipro/classes/historical-item";
import { FormIODataService } from "@features/bb-formio/services/formio-data.service";
import { IEpisodeFormData } from "@project/models/episodes/episode_formdata";
import * as moment from "moment";
import { Router } from "@angular/router";
import { UserHelperService } from "@main/users/user-helper.service";
import { UserService } from "@main/users";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { FormIORevisionService } from "@features/bb-formio/services/formio-revision.service";
import { FormRevisionDTO } from "@features/bb-formio/dto/form-revision";
import { MiProModules } from "@mipro/classes/mipro-modules";
import { Permissions } from "@main/permissions/permissions";
import { FormIOCategoryService } from "@main/formio/services/formioCategory.service";
import { AsyncMenuItem } from "@project/prescription-shared/async-menu";

@Component({
    selector: "episode-form-draft-ribbon",
    templateUrl: "./episode-form-draft.ribbon.component.html",
    styleUrls: ["./episode-form-ribbon.component.scss"]
})
export class EpisodeFormDraftRibbonComponent implements OnInit {
    private readonly confirmationService = inject(ConfirmationService);
    private readonly _cd = inject(ChangeDetectorRef);
    private readonly formIODataService = inject(FormIODataService);
    private readonly userHelperService = inject(UserHelperService);
    private readonly patientService = inject(PatientService);
    private readonly router = inject(Router);
    private readonly userService = inject(UserService);
    private readonly formIORevisionService = inject(FormIORevisionService);
    private readonly formIOCategoryService = inject(FormIOCategoryService);

    showHistorical = signal(false);
    historicalItems = signal<HistoricalItem[]>([]);
    historyLoading = signal(false);

    createdByOrganization: string;
    menuOptions: AsyncMenuItem[];

    @Input() showEllipsisButton: boolean = true;
    @Input() showActionButtons: boolean = true;
    @Input() moveButtonVisible: boolean = true;
    @Input() form: IEpisodeFormData;
    @Input() episode: IEpisode;
    @Input() deletable: boolean = true;
    @Input() hideFormsTitle: boolean = false;

    isActiveEpisode = false;
    isEpisodeLocked = false;
    creationDate: string
    canDelete = true;
    activeRevision: FormRevisionDTO = null;

    currentUrl:string = null;

    canEdit = false;

    @Output() readonly delete = new EventEmitter<string>();
    @Output() readonly refresh = new EventEmitter<void>();
    @ViewChild(OverlayPanel) userOverlayInfo: OverlayPanel;
    @ViewChild("asyncMenu") asyncMenu: any;

    get createdOn() {
        return moment(this.form.createdOn).format("DD/MM/YYYY HH:mm");
    }

    async ngOnInit(): Promise<void> {
        this.currentUrl = this.router.url;

        this.isActiveEpisode = this.episode?.state === EpisodeStatus.Active;
        this.isEpisodeLocked = this.episode?.state === EpisodeStatus.Locked;

        this.activeRevision = await this.formIORevisionService.get(this.form.formDefinition.activeRevision.id);
        this.createFormOptions();

        let canWriteModule = true;
        const moduleName = this.form.formDefinition.formCategory.name;
        const permissions = this.formIOCategoryService.getWritePermissionsForCategory(moduleName);

        if (permissions) {
            canWriteModule = (permissions.length === 0 && this.userHelperService.hasPermission(Permissions.WriteFormsInstances))
                || (this.userHelperService.hasPermission(...permissions) && this.userHelperService.hasPermission(Permissions.WriteFormsInstances));
        } else {
            canWriteModule = this.userHelperService.hasPermission(`${moduleName} - Write`);
        }

        this.canEdit = (this.userHelperService.isTopAdmin || canWriteModule) &&
            (!this.activeRevision.editableOnlyByInstantiator || this.form.userId === this.userService.currentUser.id)

        this.canDelete = this.deletable && this.canEdit;
    }

    private createFormOptions() {
        this.menuOptions = <AsyncMenuItem[]>[
            {
                id: "history",
                label: "History",
                icon: "pi pi-history",
                loading: this.historyLoading,
                asyncAction: async (event: Event) => {
                    this.historyLoading.set(true);
                    try {
                        const items = await this.formIODataService.getHistoricalItems(this.form.id);
                        this.historicalItems.set(items);
                        this.showHistorical.set(true);
                    } finally {
                        this.historyLoading.set(false);
                    }
                }
            }
        ];
    }

    view() {
        const queryParams = { formId: this.form.formDefinitionId, revisionId: this.activeRevision.id, formDataId: this.form.id, forceReadOnly: true, returnUrl : this.currentUrl  };
        this.router.navigate(["app/formio/display"], { queryParams });
    }

    edit() {
        const queryParams = { formId: this.form.formDefinitionId, revisionId: this.activeRevision.id, formDataId: this.form.id, isConsultationHistory: true,  patientId: this.patientService.currentPatientId, returnUrl : this.currentUrl };
        this.router.navigate(["app/formio/display"], { queryParams });
    }

    doDelete() {
        this.confirmationService.confirm({
            message: "Are you sure you want to delete this Form?",
            accept: async () => {
                await this.formIODataService.delete(this.form.id);
                this.delete.emit(this.form.id);
                this._cd.detectChanges();
            }
        });
    }

    complete() {
        this.confirmationService.confirm({
            message: "Are you sure you want mark Form Instance as completed?",
            accept: async () => {
                await this.formIODataService.markAsComplete(this.form.id);
                const queryParams = {
                    formId: this.form.formDefinition.id,
                    revisionId: this.activeRevision.id,
                    formDataId: this.form.id,
                    generatePdf: true,
                    returnUrl: this.currentUrl
                };
                this.router.navigate(["app/formio/display"], { queryParams });
            }
        });
    }

    get iconClassName() {
        return window.innerWidth < 576 ? "small" : "episode";
    }

    protected readonly moment = moment;
}